import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef, useState, type RefCallback } from 'react';

import { useBreakpoint } from './useBreakpoint';

declare module '@pub.tech/ardplayer-client-br24/dist/types/core/addons/tracking-piano/src/PianoWebSDKDelegate' {
	interface PianoAnalyticsWebSDK {
		getVisitorId: () => string;
	}
}

interface Meta {
	/**
	 * The version of the protocol
	 */
	version: '1.3.0';
}

interface Message<Type extends string, Payload> {
	type: Type;
	id: string | undefined;
	meta: Meta;
	requestId?: string;
	payload: Payload;
}

interface TypePayloadMap {
	init: void;
	size: { width: number; height: number };
	'view-mode': { mode: 'minimized' | 'maximized' };
	'request-context-data': { visitorId?: boolean; contextId?: boolean };
}

type Messages = MessageEvent<
	| Message<'init', TypePayloadMap['init']>
	| Message<'size', TypePayloadMap['size']>
	| Message<'view-mode', TypePayloadMap['view-mode']>
	| Message<'request-context-data', TypePayloadMap['request-context-data']>
>;

interface UseEmbedAPIOptions {
	/**
	 * Enables the `layout-mode`. This is a protocol that allows the embed to adapt its layout
	 * to the embedding context. It is only supported by certain embeds, and will be ignored by
	 * others. When enabled, the hook will send a `layout-mode` message to the embed once it
	 * has initialized, and will re-send it whenever the breakpoint changes.
	 */
	enableLayoutMode?: boolean;
}

interface UseEmbedAPIResult {
	/**
	 * The current height reported by the embed to the embedding context.
	 */
	height: number | undefined;

	/**
	 * Set to `true` once the event listeners have been installed. This should be used to load the iframe. Otherwise there
	 * is the possibility that the initial events from the iframe will  be lost.
	 */
	isInitialized: boolean;

	/**
	 * Set to `true` once the embed reports as ready.
	 */
	isEmbedInitialized: boolean;

	/**
	 * The current view mode of the embed as reported by the embed to the embedding context. This is optional and only provided if the embed supports it.
	 */
	viewMode?: 'minimized' | 'maximized';

	/**
	 * A ref callback that should be attached to the iframe DOM element. This is used to identify the iframe that is sending post messages.
	 */
	iframeRef: RefCallback<HTMLIFrameElement>;
}

export function useEmbedAPI(
	url: string | undefined | null,
	id: number | string,
	context: { type: 'overview' | 'article'; id: string },
	{ enableLayoutMode = false }: UseEmbedAPIOptions = {}
): UseEmbedAPIResult {
	const nodeRef = useRef<HTMLIFrameElement | null>(null);
	const [height, setHeight] = useState<number | undefined>(undefined);
	const [viewMode, setViewMode] = useState<'minimized' | 'maximized' | undefined>(undefined);
	const [isEmbedInitialized, setIsEmbedInitialized] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const embedOrigin = url ? new URL(url).origin : undefined;
	const isDesktopLayout = useBreakpoint('lg');

	// `isEmbedInitializedRef` is a ref that tracks whether the embed has reported its own `init` message. This is used to gate sending the
	// `layout-mode` message, which should only be sent once the embed is ready to receive it. We use a ref here instead of state because we
	// want to be able to read the value synchronously from within the `handlePostMessage` callback, without waiting for a re-render.
	const isEmbedInitializedRef = useRef(false);

	const requestContextData = useCallback(
		(message: Message<'request-context-data', TypePayloadMap['request-context-data']>) => {
			let visitorId: string | undefined;
			let contextId: { type: 'overview' | 'article'; id: string } | undefined;
			if (message.payload.visitorId) {
				visitorId = getVisitorId();
			}
			if (message.payload.contextId) {
				contextId = context;
			}

			if (nodeRef.current && embedOrigin) {
				nodeRef.current.contentWindow?.postMessage(
					{
						type: 'request-context-data-response',
						requestId: message.requestId,
						meta: { version: '1.3.0' },
						payload: { visitorId, contextId },
					},
					embedOrigin
				);
			}
		},
		[context, embedOrigin]
	);

	const sendLayoutMode = useCallback(() => {
		if (!enableLayoutMode || !embedOrigin || !isEmbedInitializedRef.current) {
			return;
		}

		nodeRef.current?.contentWindow?.postMessage(
			{
				type: 'layout-mode',
				meta: { version: '1.3.0' },
				payload: { mode: isDesktopLayout ? 'desktop' : 'mobile' },
			},
			embedOrigin
		);
	}, [enableLayoutMode, embedOrigin, isDesktopLayout]);

	const handlePostMessage = useCallback(
		(embedRendererMessage: Messages) => {
			const messageOrigin = embedRendererMessage.origin;

			if (embedOrigin === messageOrigin && id.toString() === embedRendererMessage.data.id) {
				switch (embedRendererMessage.data.type) {
					case 'init':
						isEmbedInitializedRef.current = true;
						setIsEmbedInitialized(true);
						sendLayoutMode();
						break;
					case 'size':
						setHeight(embedRendererMessage.data.payload.height);
						break;
					case 'view-mode':
						setViewMode(embedRendererMessage.data.payload.mode);
						break;
					case 'request-context-data':
						requestContextData(embedRendererMessage.data);
						break;
				}
			}
		},
		[embedOrigin, id, requestContextData, sendLayoutMode]
	);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.addEventListener('message', handlePostMessage, false);
			// eslint-disable-next-line react-hooks/set-state-in-effect, @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setIsInitialized(true);
		}

		return () => {
			if (typeof window !== 'undefined') {
				window.removeEventListener('message', handlePostMessage, false);
			}
		};
	}, [handlePostMessage]);

	// The iframe DOM node can be unmounted and replaced by a consumer (e.g. a layout that switches
	// between structurally different roots on a breakpoint change). Resetting `isEmbedInitializedRef`
	// here means `sendLayoutMode` can't fire again until a *fresh* `init` message arrives from
	// whatever now occupies this node.
	const iframeRef: RefCallback<HTMLIFrameElement> = useCallback((node) => {
		nodeRef.current = node;
		isEmbedInitializedRef.current = false;
	}, []);

	// Re-sends when the breakpoint changes while an already-initialized iframe is mounted (the
	// `init` case above only fires once per embed lifecycle). `sendLayoutMode` re-checks readiness
	// via the ref internally, so calling it unconditionally here is safe - this only has an
	// observable effect once the embed has actually initialized.
	useEffect(() => {
		sendLayoutMode();
	}, [sendLayoutMode]);

	return { height, isInitialized, isEmbedInitialized, viewMode, iframeRef };
}

function getVisitorId(): string | undefined {
	let visitorId = window.pa?.getVisitorId();

	if (!visitorId) {
		const cookie = Cookies.get('_pcid');
		if (cookie) {
			try {
				// _pcid is a Piano-managed cookie with shape { browserId: string }
				visitorId = JSON.parse(cookie)?.browserId;
			} catch {
				// ignore unparseable cookie
			}
		}
	}
	return visitorId;
}
